import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { ColumnFiltersState } from "@tanstack/react-table";
import { Axios } from "api/axios";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
const API_URL = process.env.REACT_APP_BASE_URL;
const TOKEN = "a94c7ac0083ae190f0fe61143a7547799b5334c4";

const headers = {
  Authorization: `Token ${TOKEN}`,
  "Content-Type": "application/json",
};

export const fetchProductsAndServices = createAsyncThunk(
  "productsandservices/fetchProductsAndServices",
  async ({
    activeTab,
    page,
    pageSize,
    sort,
    order,
    search,
    filters,
  }: {
    activeTab: string;
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any;
  }) => {
    try {
      const params = new URLSearchParams();

      // pagination
      params.append("page", page.toString());
      params.append("page_size", pageSize.toString());

      // search
      if (search) params.append("search", search.trim());

      // sorting
      if (sort) {
        const orderingValue = order === "desc" ? `-${sort}` : sort;
        params.append("ordering", orderingValue);
      }

      // filters
      if (filters && filters.length > 0) {
        filters.forEach(({ id, value }: any) => {
          if (id && value !== undefined && value !== null && value !== "null") {
            params.append(id, value);
          }
        });
      }

      const response = await Axios.get(
        `/products_and_services/type/${activeTab}?${params.toString()}`,
      );

      return {
        data: response.data.data || response.data || [],
        rowCount: response.data.count || response.data.length || 0,
      };
    } catch (error: any) {
      console.error(
        "fetchProductsAndServices error:",
        error.response?.data || error.message,
      );
      throw new Error("Failed to fetch products and services");
    }
  },
);

export const fetchProductsAndServicesByType = createAsyncThunk(
  "productsandservices/fetchProductsAndServicesByType",
  async (activeTab: string) => {
    try {
      const response: any = await Axios.get(
        `/products_and_services/type/${activeTab}`,
      );

      return {
        data: response?.data?.data || response?.data || [],
        rowCount: response.data.count || response.data.length || 0,
      };
    } catch (error: any) {
      console.error(
        "fetchProductsAndServices error:",
        error.response?.data || error.message,
      );
      throw new Error("Failed to fetch products and services");
    }
  },
);

export const fetchProductsAndServicesById = createAsyncThunk(
  "productsandservices/fetchById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response: any = await Axios.get(`/products_and_services/${id}/`);

      return response.data || response.data.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch products and services by ID",
      );
    }
  },
);

export const fetchProductsAndServicesByDropDown = createAsyncThunk(
  "productsandservices/fetchByDropDown",

  async (_, { rejectWithValue }) => {
    try {
      const response = await Axios.get(`/products_and_services/dropdown/`);

      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch products and services list",
      );
    }
  },
);

export const updateProductsAndServices = createAsyncThunk(
  "productsandservices/updateProductsAndServices",
  async (data: any, { rejectWithValue }) => {
    try {
      const formData = data.data; // Extract FormData from the data object
      const response: any = await Axios.put(
        `/products_and_services/${data.id}/update/`,
        formData,
      );
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to update products and services",
      );
    }
  },
);

export const updateStatusInProductsAndServices = createAsyncThunk(
  "productsandservices/updateStatusInProductsAndServices",
  async (data: any, { rejectWithValue }) => {
    try {
      const payload = { is_active: data.is_active };
      const response: any = await Axios.patch(
        `/products_and_services/${data.id}/update/`,
        payload,
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data ||
          "Failed to update status in products and services",
      );
    }
  },
);

export const addProductsAndServices = createAsyncThunk(
  "productsandservices/addProductsAndServices",
  async (data: any, { rejectWithValue }) => {
    try {
      const response: any = await Axios.post(
        `/products_and_services/create/`,
        data,
      );

      return response;
    } catch (err: any) {
      console.error("addProductsAndServices: Error occurred:", err);
      console.error("addProductsAndServices: Error response:", err.response);
      return rejectWithValue(
        err.response?.data || "Failed to add products and services",
      );
    }
  },
);

export const deleteProductsAndServices = createAsyncThunk(
  "productsandservices/delete",
  async (id: number, { rejectWithValue }) => {
    try {
      await Axios.delete(`/products_and_services/${id}/`);
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

export const fetchCategories = createAsyncThunk(
  "productsandservices/fetchCategories",
  async (itemType: string, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `/products_and_services/categories/?item_type=${itemType}`,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch categories",
      );
    }
  },
);

export const fetchSubcategories = createAsyncThunk(
  "productsandservices/fetchSubcategories",
  async (
    { item_type, categoryId }: { item_type: string; categoryId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await Axios.get(
        `/products_and_services/subcategories/${categoryId}/?item_type=${item_type}`,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch subcategories",
      );
    }
  },
);

export const fetchMakes = createAsyncThunk(
  "productsandservices/fetchMakes",
  async (
    {
      categoryId,
      subcategoryId,
    }: { categoryId: string; subcategoryId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await Axios.get(
        `/products_and_services/makes/${categoryId}/${subcategoryId}/`,
      );
      return response?.data?.data || response?.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Failed to fetch makes");
    }
  },
);

export const fetchModels = createAsyncThunk(
  "productsandservices/fetchModels",
  async (
    {
      categoryId,
      subcategoryId,
      makeId,
    }: { categoryId: string; subcategoryId: string; makeId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await Axios.get(
        `/products_and_services/models/${categoryId}/${subcategoryId}/${makeId}/`,
      );
      return response?.data?.data || response?.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Failed to fetch models");
    }
  },
);
// only for module listing
// only for module listing
export const fetchModel = createAsyncThunk(
  "productsandservices/fetchModel",
  async (typ_of_item: any, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${API_ENDPOINT?.MASTERS?.PRODUCT?.MODEL_DROPDOWN}`,
      );

      return response.data;
    } catch (error: any) {
      console.error(" API Error:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || "Failed to fetch models");
    }
  },
);

export const fetchProductsDropDownBasedOnModel = createAsyncThunk(
  "enquiry/fetchProductsDropDownBasedOnModel",
  async (model_id: any, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${API_ENDPOINT?.MASTERS?.PRODUCT?.DROPDOWN_BY_MODEL(model_id)}`,
      );
      return response.data.data || response?.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch products",
      );
    }
  },
);

export const fetchProductsAndServicesBasedOnModel = createAsyncThunk(
  "enquiry/fetchProductsAndServicesBasedOnModel",
  async (
    { item_type, model_id }: { item_type: string; model_id: string },
    { rejectWithValue },
  ) => {
    try {
      const response: any = await Axios.get(
        `/products_and_services/${item_type}s/${model_id}/`,
      );

      return response.data;
    } catch (err: any) {
      console.error("API Error:", err.response?.data || err.message);
      return rejectWithValue(
        err.response?.data || "Failed to fetch products/services",
      );
    }
  },
);

export const exportProductsAndServicesCSV = async ({
  search,
  filters,
  type,
}: {
  search?: string;
  filters?: any;
  type?: string;
}) => {
  const params = new URLSearchParams();

  params.append("export", "excel");

  if (search) params.append("search", search);

  if (filters && filters.length > 0) {
    filters.forEach(
      ({
        filterField,
        filterValue,
      }: {
        filterField: string;
        filterValue: any;
      }) => {
        if (filterField && filterValue !== undefined && filterValue !== null) {
          if (typeof filterValue === "object" && filterValue !== null) {
            Object.entries(filterValue).forEach(([key, value]) => {
              if (value !== undefined && value !== null && value !== "") {
                params.append(key, String(value));
              }
              if (
                (filterValue.from_date || filterValue.to_date) &&
                (filterField === "created_at" || filterField === "updated_at")
              ) {
                if (filterField === "created_at") {
                  filterField = "created";
                } else {
                  filterField = "updated";
                }
                params.append("filter_by", filterField);
              }
            });
          } else {
            params.append(filterField, filterValue);
          }
        }
      },
    );
  }

  const response = await Axios.get(
    `/products_and_services/type/${type}/?${params.toString()}`,
    {
      responseType: "blob",
    },
  );

  return {
    blob: response.data,
    filename: `${
      response.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") || "productsandservices"
    }_${new Date().toISOString().split("T")[0]}.csv`,
  };
};
